import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import PopupWrapper from 'components/Popups/Wrapper';
import { updateOwnedCommunitySubscriptionSlots } from 'services/users';
import * as S from './styled';
import { InitialsAvatar } from './styled';

/** Two-letter (or one) initials for avatar tiles - never the full name. */
function getCommunityInitials(raw: string): string {
  const name = raw.trim();
  if (!name) return '?';
  const words = name.split(/\s+/).filter((w) => w.length > 0);
  const pick = (w: string) => {
    const m = w.match(/[a-zA-Z0-9]/);
    return m ? m[0] : w.charAt(0);
  };
  if (words.length >= 2) {
    return (pick(words[0]) + pick(words[1])).toUpperCase();
  }
  const letters = name.replace(/[^a-zA-Z0-9]/g, '');
  if (letters.length >= 2) {
    return (letters[0] + letters[1]).toUpperCase();
  }
  if (letters.length === 1) {
    return letters[0].toUpperCase();
  }
  return name.charAt(0).toUpperCase();
}

export type CommunitySlotRow = {
  id: string;
  name: string;
  logo: string | null;
  subscriptionSlotActive?: boolean;
};

type CommunitySlotsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  maxSlots: number;
  userId: string;
  communities: CommunitySlotRow[];
  /** Return `false` to keep the dialog open (e.g. checkout still blocked). */
  onSaved: () => void | false | Promise<void | false>;
};

function buildInitialSelection(
  rows: CommunitySlotRow[],
  max: number,
): Set<string> {
  const activeFirst = rows.filter((r) => r.subscriptionSlotActive !== false);
  const ids: string[] = [];
  for (const r of activeFirst) {
    if (ids.length >= max) break;
    ids.push(r.id);
  }
  for (const r of rows) {
    if (ids.length >= max) break;
    if (!ids.includes(r.id)) ids.push(r.id);
  }
  return new Set(ids);
}

const CommunitySlotsModal: React.FC<CommunitySlotsModalProps> = ({
  isOpen,
  onClose,
  maxSlots,
  userId,
  communities,
  onSaved,
}) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen || communities.length === 0) return;
    setSelected(buildInitialSelection(communities, maxSlots));
  }, [isOpen, communities, maxSlots]);

  const toggle = useCallback(
    (id: string) => {
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else if (next.size < maxSlots) {
          next.add(id);
        } else {
          // At capacity: swap the clicked row in (e.g. single-slot plan = radio behavior).
          const kept = [...next];
          kept.shift();
          next.clear();
          kept.forEach((x) => next.add(x));
          next.add(id);
        }
        return next;
      });
    },
    [maxSlots],
  );

  const canSave = selected.size === maxSlots && maxSlots > 0;

  const onSave = async () => {
    if (!canSave || !userId) return;
    setSubmitting(true);
    try {
      await updateOwnedCommunitySubscriptionSlots(userId, [...selected]);
      const closeAfter = await onSaved();
      if (closeAfter !== false) {
        onClose();
      }
    } catch {
      toast.error(t('communitySlots.saveError'));
    } finally {
      setSubmitting(false);
    }
  };

  const empty = useMemo(() => communities.length === 0, [communities.length]);

  return (
    <PopupWrapper isOpen={isOpen}>
      <S.Panel
        role="dialog"
        aria-modal="true"
        aria-labelledby="community-slots-title"
      >
        <S.PanelHeader>
          <S.Title id="community-slots-title">
            {t('communitySlots.modalTitle')}
          </S.Title>
          <S.Intro>
            {maxSlots === 1
              ? t('communitySlots.modalIntroSingle')
              : t('communitySlots.modalIntro', { max: maxSlots })}
          </S.Intro>
          <S.OnceNote>{t('communitySlots.onceOnlyNote')}</S.OnceNote>
          <S.CountLine>
            {t('communitySlots.selectedCount', {
              selected: selected.size,
              max: maxSlots,
            })}
          </S.CountLine>
        </S.PanelHeader>
        {empty ? (
          <S.Intro style={{ padding: '16px 20px' }}>
            {t('communitySlots.emptyOwned')}
          </S.Intro>
        ) : (
          <S.List>
            {communities.map((c) => {
              const isOn = selected.has(c.id);
              return (
                <S.Row
                  key={c.id}
                  $selected={isOn}
                  onClick={() => toggle(c.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggle(c.id);
                    }
                  }}
                  role="checkbox"
                  aria-checked={isOn}
                  tabIndex={0}
                >
                  {c.logo ? (
                    <S.Avatar src={c.logo} alt="" />
                  ) : (
                    <InitialsAvatar aria-hidden>
                      {getCommunityInitials(c.name)}
                    </InitialsAvatar>
                  )}
                  <S.RowText>
                    <S.RowName>{c.name}</S.RowName>
                    <S.RowMeta>
                      {c.subscriptionSlotActive === false
                        ? t('communitySlots.statusInactive')
                        : t('communitySlots.statusActive')}
                    </S.RowMeta>
                  </S.RowText>
                  <S.Check aria-hidden>{isOn ? '✓' : ''}</S.Check>
                </S.Row>
              );
            })}
          </S.List>
        )}
        <S.Footer>
          <S.Btn type="button" onClick={onClose} disabled={submitting}>
            {t('communitySlots.cancel')}
          </S.Btn>
          <S.Btn
            type="button"
            $primary
            disabled={!canSave || submitting || empty}
            onClick={() => void onSave()}
          >
            {submitting ? t('communitySlots.saving') : t('communitySlots.save')}
          </S.Btn>
        </S.Footer>
      </S.Panel>
    </PopupWrapper>
  );
};

export default CommunitySlotsModal;
